import { ListingEntry } from 'graphql/listings/queries';
import React, { createContext, FC, useContext } from 'react';
import { AccountingType } from 'v2-types/order';

import { ModalState, OpenConfirmationModalType, useCharges } from './useCharges';

type ChargesContextType = {
  orderId: string;
  accounting: AccountingType[];
  updateAccounting: (data: AccountingType[]) => void;
  isFetching: boolean;
  setHideTax: React.Dispatch<React.SetStateAction<boolean>>;
  hideTax: boolean;
  policyIsLoading: boolean;
  handleToggleDisclosureSection: () => void;
  disclosureSection: string;
  showNewInfoAlert: boolean;
  chargesLetter?: string;
  accountingFilter?: string
  filterCharges: (charges: AccountingType[]) => AccountingType[];
  textPredectiveCharges: ListingEntry[];
  reloadAccounting: () => void;
  modalState: ModalState;
  updateModalState: (newModalState: ModalState) => void;
  lockedCharges: ListingEntry[];
  setAccounting: React.Dispatch<React.SetStateAction<AccountingType[]>>;
  openConfirmationModal: (params: OpenConfirmationModalType) => void;
  handleCloseModal: () => void;
  hideAddEntry: boolean
  hideEndorsementCode: boolean
};

const GeneralChargesContext = createContext<ChargesContextType | null>(null);

type Props = {
  orderType: string;
  children: React.ReactNode;
  hideAddEntry?: boolean;
  hideEndorsementCode?: boolean;
  chargesLetter: string;
  accountingFilter?: never
} | {
  orderType: string;
  children: React.ReactNode;
  hideAddEntry?: boolean;
  hideEndorsementCode?: boolean;
  accountingFilter: string
  chargesLetter?: never;
}

export const GeneralChargesProvider: FC<Props> = ({
  orderType,
  chargesLetter,
  children,
  accountingFilter,
  hideAddEntry = false,
  hideEndorsementCode = true
}) => {
  const params = chargesLetter !== undefined ? ({ orderType, currentLetter: chargesLetter }) : ({ orderType, accountingFilter });
  const {
    accounting,
    updateAccounting,
    isFetching,
    setHideTax,
    hideTax,
    showNewInfoAlert,
    handleToggleDisclosureSection,
    disclosureSection,
    policyIsLoading,
    filterCharges,
    textPredectiveCharges,
    reloadAccounting,
    modalState,
    updateModalState,
    lockedCharges,
    setAccounting,
    orderId,
    openConfirmationModal,
    handleCloseModal,
  } = useCharges(params);

  return (
    <GeneralChargesContext.Provider
      value={{
        orderId,
        accounting,
        updateAccounting,
        isFetching,
        setHideTax,
        hideTax,
        policyIsLoading,
        showNewInfoAlert,
        reloadAccounting,
        chargesLetter,
        accountingFilter,
        handleToggleDisclosureSection,
        disclosureSection,
        filterCharges,
        textPredectiveCharges,
        modalState,
        updateModalState,
        lockedCharges,
        setAccounting,
        openConfirmationModal,
        handleCloseModal,
        hideAddEntry,
        hideEndorsementCode
      }}
    >
      {children}
    </GeneralChargesContext.Provider>
  );
};

export const useGeneralChargesContext = () => {
  const methods = useContext(GeneralChargesContext)!;
  return methods;
};
